/**
 * Reusable markdown→HTML page renderer for legal documents.
 *
 * Lightweight, no `react-markdown` dependency — supports headings (# / ##),
 * bullets, bold (**text**), italics (*text*), and paragraph breaks.
 * Suitable for our well-known controlled markdown corpus only.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Loader2, ArrowLeft } from "lucide-react";

function renderInline(text) {
  // Bold
  let out = text.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");
  // Italic
  out = out.replace(/(^|\s)\*(.+?)\*/g, "$1<em>$2</em>");
  // Inline code
  out = out.replace(/`([^`]+)`/g, "<code>$1</code>");
  return out;
}

function renderMarkdown(md) {
  const lines = (md || "").split("\n");
  const blocks = [];
  let para = [];
  let list = [];
  const flush = () => {
    if (para.length) {
      blocks.push(<p key={blocks.length} dangerouslySetInnerHTML={{ __html: renderInline(para.join(" ")) }} />);
      para = [];
    }
    if (list.length) {
      blocks.push(
        <ul key={blocks.length} className="list-disc pl-5 space-y-1">
          {list.map((li, i) => (
            <li key={i} dangerouslySetInnerHTML={{ __html: renderInline(li) }} />
          ))}
        </ul>
      );
      list = [];
    }
  };
  for (const raw of lines) {
    const line = raw.trimEnd();
    if (!line.trim()) { flush(); continue; }
    if (line.startsWith("# ")) {
      flush();
      blocks.push(<h1 key={blocks.length} className="font-heading text-3xl font-semibold tracking-tight text-clinical-text mt-2">{line.slice(2).trim()}</h1>);
    } else if (line.startsWith("## ")) {
      flush();
      blocks.push(<h2 key={blocks.length} className="font-heading text-lg font-semibold text-clinical-text mt-6">{line.slice(3).trim()}</h2>);
    } else if (line.startsWith("- ")) {
      list.push(line.slice(2).trim());
    } else {
      if (list.length) flush();
      para.push(line.trim());
    }
  }
  flush();
  return blocks;
}

export default function LegalPage({ kind }) {
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    api.get(`/legal/${kind}`)
      .then((res) => { if (alive) setDoc(res.data); })
      .catch(() => {})
      .finally(() => { if (alive) setLoading(false); });
  }, [kind]);

  return (
    <div className="min-h-screen bg-clinical-bg/40 py-10 px-4 sm:px-6">
      <div className="mx-auto max-w-3xl">
        <Link to="/login" className="inline-flex items-center gap-1.5 text-xs text-clinical-subtext hover:text-clinical-text mb-4"
              data-testid={`legal-${kind}-back`}>
          <ArrowLeft className="h-3.5 w-3.5" /> Back to sign in
        </Link>
        <div className="card-surface p-6 sm:p-10 prose prose-sm max-w-none" data-testid={`legal-${kind}-content`}>
          {loading ? (
            <div className="flex items-center gap-2 text-clinical-subtext text-sm">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading…
            </div>
          ) : !doc ? (
            <p className="text-clinical-subtext text-sm">Unable to load this document right now.</p>
          ) : (
            <div className="space-y-2 text-sm leading-relaxed text-clinical-text">
              {renderMarkdown(doc.content_markdown)}
              <p className="text-xs text-clinical-subtext pt-4 border-t border-clinical-border mt-6">
                Version {doc.version} · Effective {doc.effective_date}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
